import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { DirectoryState } from "../types/types";

const API_BASE_URL =
  "https://fnp5vd20r2.execute-api.us-east-1.amazonaws.com/dev/directories";

const initialState: DirectoryState = {
  name: null,
  id: null,
  path: [],
  files: [],
  directories: [],
  loading: false,
  error: null,
};

export const fetchRootDirectory = createAsyncThunk(
  "directory/fetchRoot",
  async () => {
    const response = await fetch(API_BASE_URL, { method: "GET" });
    if (!response.ok) throw new Error("Failed to fetch root directory");
    return response.json();
  }
);

export const fetchDirectory = createAsyncThunk(
  "directory/fetch",
  async ({
    directoryId,
    addToPath,
  }: {
    directoryId: string | null;
    addToPath: boolean;
  }) => {
    const url = directoryId
      ? `${API_BASE_URL}/${directoryId}`
      : API_BASE_URL;
    const response = await fetch(url, { method: "GET" });
    if (!response.ok) throw new Error("Failed to fetch directory");
    const data = await response.json();
    return { data, addToPath };
  }
);

export const directorySlice = createSlice({
  name: "directory",
  initialState,
  reducers: {
    removePath: (state, action: PayloadAction<number>) => {
      state.path.length = action.payload + 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRootDirectory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRootDirectory.fulfilled, (state, action) => {
        state.loading = false;
        state.name = action.payload.name;
        state.id = action.payload.id;
        state.files = action.payload.files;
        state.directories = action.payload.directories;
        state.path = [{ id: action.payload.id, name: action.payload.name }];
      })
      .addCase(fetchRootDirectory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred";
      })
      .addCase(fetchDirectory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDirectory.fulfilled, (state, action) => {
        state.loading = false;
        const { data, addToPath } = action.payload;
        state.name = data.name;
        state.id = data.id;
        state.files = data.files;
        state.directories = data.directories;
        if (addToPath) {
          state.path = [...state.path, { id: data.id, name: data.name }];
        }
      })
      .addCase(fetchDirectory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred";
      });
  },
});

export const { removePath } = directorySlice.actions;

export default directorySlice.reducer;
